import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Button,
  ModalFooter,
  TextField,
} from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import createDecorator from 'final-form-focus';

import FormFieldsGenerator from '../../../../../../../shared/FormFieldsGenerator';

const focusOnError = createDecorator();

function DuplicateHoldingModal({
  open,
  onClose,
  onConfirm,
  originalHolding,
  template,
  controlledVocab,
  currentHoldingsCount,
  isMultiple = false,
}) {
  const [copiesCount, setCopiesCount] = useState(1);

  // Prepare initial values for duplication
  const getInitialValues = () => {
    const duplicatedData = {
      ...originalHolding,
      // Clear unique identifiers
      id: undefined,
      holdingHrid: '',
      holdingCopyNumber: currentHoldingsCount + 1,
    };

    return duplicatedData;
  };

  // Handle form submission
  const handleSubmit = (values) => {
    if (isMultiple) {
      // For multiple duplication, pass the count
      onConfirm(copiesCount);
    } else {
      // For single duplication, pass the edited values
      onConfirm(values);
    }
    onClose();
    return undefined;
  };

  // Handle multiple copies count change
  const handleCopiesCountChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setCopiesCount(Math.max(1, value || 1));
  };

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={getInitialValues()}
      mutators={{ ...arrayMutators }}
      decorators={[focusOnError]}
      render={({ handleSubmit: formSubmit, submitting }) => {
        // Create footer JSX without memoization to avoid hooks rule violation
        const renderFooter = (
          <ModalFooter>
            <Button
              onClick={formSubmit}
              buttonStyle="primary"
              marginBottom0
              disabled={submitting || (isMultiple && copiesCount < 1)}
            >
              <FormattedMessage
                id={
                  isMultiple
                    ? 'ui-plugin-inventory-templates.holdingManagement.duplicateMultipleModal.create'
                    : 'ui-plugin-inventory-templates.holdingManagement.duplicateModal.duplicate'
                }
                values={{ count: copiesCount }}
                defaultMessage={
                  isMultiple ? 'Create {count} Holdings' : 'Duplicate Holding'
                }
              />
            </Button>
            <Button onClick={onClose} buttonStyle="secondary" marginBottom0>
              <FormattedMessage
                id="ui-plugin-inventory-templates.holdingManagement.duplicateModal.cancel"
                defaultMessage="Cancel"
              />
            </Button>
          </ModalFooter>
        );

        return (
          <Modal
            open={open}
            onClose={onClose}
            footer={renderFooter}
            label={
              <FormattedMessage
                id={
                  isMultiple
                    ? 'ui-plugin-inventory-templates.holdingManagement.duplicateMultipleModal.title'
                    : 'ui-plugin-inventory-templates.holdingManagement.duplicateModal.title'
                }
                defaultMessage={
                  isMultiple ? 'Duplicate Multiple Holdings' : 'Duplicate Holding'
                }
              />
            }
            size={isMultiple ? 'medium' : 'large'}
            wrappingElement="form"
          >
            <form onSubmit={formSubmit}>
              <div style={{ padding: '20px' }}>
                {isMultiple ? (
                  // Multiple duplication interface
                  <div>
                    <p style={{ marginBottom: '20px', color: '#6b7280' }}>
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.holdingManagement.duplicateMultipleModal.description"
                        values={{
                          holdingNumber:
                            originalHolding?.holdingCopyNumber || '?',
                        }}
                        defaultMessage="How many copies of holding {holdingNumber} do you want to create?"
                      />
                    </p>

                    <div style={{ marginBottom: '20px' }}>
                      <TextField
                        label={
                          <FormattedMessage
                            id="ui-plugin-inventory-templates.holdingManagement.duplicateMultipleModal.copiesLabel"
                            defaultMessage="Number of copies"
                          />
                        }
                        value={copiesCount}
                        onChange={handleCopiesCountChange}
                        type="number"
                        min="1"
                        required
                      />
                    </div>

                    <div
                      style={{
                        backgroundColor: '#f0f9ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: '4px',
                        padding: '12px',
                      }}
                    >
                      <p
                        style={{ margin: 0, fontSize: '14px', color: '#1e40af' }}
                      >
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.holdingManagement.duplicateMultipleModal.info"
                          values={{ count: copiesCount }}
                          defaultMessage="This will create {count} new holding(s) based on the original holding data."
                        />
                      </p>
                    </div>
                  </div>
                ) : (
                  // Single duplication interface with editable form
                  <div>
                    <p style={{ marginBottom: '20px', color: '#6b7280' }}>
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.holdingManagement.duplicateModal.description"
                        values={{
                          holdingNumber:
                            originalHolding?.holdingCopyNumber || '?',
                        }}
                        defaultMessage="Review and edit the holding {holdingNumber} details before duplicating. Required fields are marked with an asterisk."
                      />
                    </p>

                    {/* Generate form fields from template */}
                    <FormFieldsGenerator
                      key="duplicate-holding-template-fields"
                      formValues={template?.templateFields?.holdings || []}
                      pageName="" // Empty pageName so fields use direct field names
                      controlledVocab={controlledVocab}
                    />
                  </div>
                )}
              </div>
            </form>
          </Modal>
        );
      }}
    />
  );
}

DuplicateHoldingModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  originalHolding: PropTypes.object,
  template: PropTypes.object,
  controlledVocab: PropTypes.object,
  currentHoldingsCount: PropTypes.number,
  isMultiple: PropTypes.bool,
};

export default DuplicateHoldingModal;
